import isEqual from 'lodash/isEqual';
import type { MangoQuerySelector } from 'rxdb';

import { isFirstClassDocumentQuery } from '../database/standardQueries';
import { AnyDocument } from '../types';

function negateOperator(operator: string, value: unknown) {
  switch (operator) {
    case '$eq':
      return { $ne: value };
    case '$ne':
      return { $eq: value };
    case '$in':
      return { $nin: value };
    case '$nin':
      return { $in: value };
    case '$gt':
      return { $lte: value };
    case '$lt':
      return { $gte: value };
    case '$gte':
      return { $lt: value };
    case '$lte':
      return { $gt: value };
    case '$exists':
      return { $exists: !value };
    case '$not':
      return value;
    default:
      return { $eq: value };
  }
}

function removeFirstClassDocQuery(
  query: MangoQuerySelector<AnyDocument> | MangoQuerySelector<AnyDocument>[],
) {
  if (Array.isArray(query)) {
    return query.filter((q) => !isEqual(q, isFirstClassDocumentQuery));
  }

  return query;
}

export function negateQuery(_query: MangoQuerySelector<AnyDocument>) {
  const negatedQuery: MangoQuerySelector<AnyDocument> = {};
  const query = removeFirstClassDocQuery(_query);

  // Base case: if query is undefined
  if (!query) {
    return negatedQuery;
  }

  if (!Array.isArray(query) && query.$and) {
    const andQueries = removeFirstClassDocQuery(query.$and) as MangoQuerySelector<AnyDocument>[];
    const negatedConditions: MangoQuerySelector<AnyDocument>[] = andQueries.map((condition) =>
      negateQuery(condition));
    return { $and: negatedConditions };
  }

  if (!Array.isArray(query) && query.$or) {
    const orQueries = removeFirstClassDocQuery(query.$or) as MangoQuerySelector<AnyDocument>[];
    const negatedConditions: MangoQuerySelector<AnyDocument>[] = orQueries.map((condition) =>
      negateQuery(condition));
    return { $and: negatedConditions };
  }

  // For individual conditions
  const queryRecord = query as { [key: string]: unknown; };
  const negatedRecord = negatedQuery as { [key: string]: unknown; };
  for (const key in queryRecord) {
    if (Object.hasOwn(queryRecord, key)) {
      const subQuery = queryRecord[key];

      if (typeof subQuery === 'object' && !Array.isArray(subQuery) && subQuery !== null) {
        negatedRecord[key] = {};
        const subQueryRecord = subQuery as { [key: string]: unknown; };

        for (const operator in subQueryRecord) {
          if (Object.hasOwn(subQueryRecord, operator)) {
            Object.assign(negatedRecord[key] as object, negateOperator(operator, subQueryRecord[operator]));
          }
        }
      } else {
        // Treat it as a direct value (implicit $eq)
        negatedRecord[key] = { $ne: subQuery };
      }
    }
  }

  return negatedQuery;
}
